import React, { useCallback, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import {
  MAX_PROFILE_FILES,
  MAX_PROFILE_FILE_SIZE_BYTES,
  PROFILE_FILE_ACCEPT,
  BUILD_FILE_PRIVACY_TOOLTIP,
  UPDATE_FILE_PRIVACY_TOOLTIP,
  PROFILE_FILE_TYPES_LABEL,
} from '../constants';

const PrivacyInfoButton = ({ title }) => (
  <Tooltip
    title={title}
    arrow
    placement='top'
    slotProps={{
      tooltip: {
        sx: {
          maxWidth: 320,
          fontSize: 12,
          lineHeight: 1.45,
          p: 1.25,
        },
      },
    }}
  >
    <IconButton
      size='small'
      aria-label='File privacy information'
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      sx={{
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 1,
        color: 'text.secondary',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Tooltip>
);

const DocumentUploadZone = ({
  onUploaded,
  compact = false,
  disabled = false,
  currentFileCount = 0,
  isUpdate = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const remainingSlots = Math.max(0, MAX_PROFILE_FILES - currentFileCount);
  const atLimit = remainingSlots <= 0;
  const privacyTooltip = isUpdate ? UPDATE_FILE_PRIVACY_TOOLTIP : BUILD_FILE_PRIVACY_TOOLTIP;

  const uploadFiles = useCallback(
    async (acceptedFiles, fileRejections = []) => {
      if (disabled) return;

      fileRejections.forEach(({ file, errors }) => {
        if (errors.some((error) => error.code === 'file-invalid-type') && file.type?.startsWith('video/')) {
          toast.error(`${file.name}: video is not supported`);
          return;
        }
        if (errors.some((error) => error.code === 'file-too-large')) {
          toast.error(`${file.name} exceeds the 100MB limit`);
        } else if (errors.some((error) => error.code === 'file-invalid-type')) {
          toast.error(`${file.name}: unsupported file type`);
        }
      });

      if (!acceptedFiles?.length) return;

      if (atLimit) {
        toast.error(`You can upload up to ${MAX_PROFILE_FILES} files`);
        return;
      }

      const filesToUpload = acceptedFiles.slice(0, remainingSlots);
      if (acceptedFiles.length > remainingSlots) {
        toast.error(`You can upload up to ${MAX_PROFILE_FILES} files`);
      }

      setIsUploading(true);
      let successCount = 0;

      for (const file of filesToUpload) {
        if (file.type?.startsWith('video/')) {
          toast.error(`${file.name}: video is not supported`);
          continue;
        }
        if (file.size > MAX_PROFILE_FILE_SIZE_BYTES) {
          toast.error(`${file.name} exceeds the 100MB limit`);
          continue;
        }

        try {
          await onUploaded(file);
          successCount += 1;
        } catch (error) {
          console.error(error);
          const message =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            `Failed to upload ${file.name}`;
          toast.error(message);
        }
      }

      if (successCount > 0) {
        toast.success(successCount === 1 ? 'Document uploaded' : `${successCount} documents uploaded`);
      }
      setIsUploading(false);
    },
    [atLimit, disabled, onUploaded, remainingSlots],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: uploadFiles,
    accept: PROFILE_FILE_ACCEPT,
    multiple: true,
    noClick: compact,
    disabled: disabled || isUploading || atLimit,
    maxSize: MAX_PROFILE_FILE_SIZE_BYTES,
  });

  if (compact) {
    return (
      <Box mb={1.5}>
        <Box
          {...getRootProps()}
          sx={{
            position: 'relative',
            border: '1px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            pr: 5,
            bgcolor: isDragActive ? 'action.hover' : '#fafafa',
            cursor: disabled || isUploading || atLimit ? 'default' : 'pointer',
          }}
        >
          <input {...getInputProps()} />
          <PrivacyInfoButton title={privacyTooltip} />
          <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
            <Typography variant='body2' color='text.secondary'>
              {isUploading
                ? 'Uploading…'
                : isDragActive
                  ? 'Drop files here'
                  : atLimit
                    ? `Maximum of ${MAX_PROFILE_FILES} files reached`
                    : 'Drag more files here or choose files'}
            </Typography>
            <Button
              size='small'
              variant='outlined'
              disabled={disabled || isUploading || atLimit}
              onClick={(event) => {
                event.stopPropagation();
                open();
              }}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              {isUploading ? <CircularProgress size={16} /> : 'Choose files'}
            </Button>
          </Box>
        </Box>
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          mt={0.75}
          sx={{ fontSize: 11, lineHeight: 1.5 }}
        >
          {PROFILE_FILE_TYPES_LABEL}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={2}>
      <Box
        {...getRootProps()}
        sx={{
          position: 'relative',
          border: '1px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          px: 2,
          py: 3,
          textAlign: 'center',
          bgcolor: isDragActive ? 'action.hover' : '#fafafa',
          cursor: disabled || isUploading || atLimit ? 'default' : 'pointer',
        }}
      >
        <input {...getInputProps()} />
        <PrivacyInfoButton title={privacyTooltip} />
        <CloudUploadOutlinedIcon color='action' sx={{ fontSize: 36, mb: 1 }} />
        <Typography variant='body2' fontWeight={600} mb={1.5}>
          {isUploading
            ? 'Uploading…'
            : isDragActive
              ? 'Drop files here'
              : atLimit
                ? `Maximum of ${MAX_PROFILE_FILES} files reached`
                : 'Drag & drop files here'}
        </Typography>
        <Button
          variant='outlined'
          size='small'
          disabled={disabled || isUploading || atLimit}
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
          sx={{ textTransform: 'none' }}
        >
          {isUploading ? <CircularProgress size={16} /> : 'Choose files'}
        </Button>
      </Box>
      <Typography
        variant='caption'
        color='text.secondary'
        display='block'
        mt={0.75}
        sx={{ fontSize: 11, lineHeight: 1.5 }}
      >
        {PROFILE_FILE_TYPES_LABEL}
      </Typography>
    </Box>
  );
};

export default DocumentUploadZone;
